import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  patchServiceAlertReqDtoSchema,
  useAdminServiceAlertsControllerFindById,
  useAdminServiceAlertsControllerUpdate,
} from "service";
import { PatchServiceAlertReqDto } from "service/api/estabrookSchemas";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";
import { queryClient } from "../../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import {
  BaseFormHookProps,
  BaseFormHookReturn,
} from "../../../../shared/types/formTypes";

interface UseServiceAlertFormProps extends BaseFormHookProps {
  serviceAlertId: string;
}

export const useServiceAlertForm = ({
  serviceAlertId,
  onSuccess,
}: UseServiceAlertFormProps): BaseFormHookReturn<PatchServiceAlertReqDto> & {
  data?: any;
} => {
  const { mutate, isLoading } = useAdminServiceAlertsControllerUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v2/admin/service-alerts",
          operationId: "adminServiceAlertsControllerFindMany",
          variables: {},
        }),
      );
      resetForm();
      toast.success("Service Alert Saved");
      // Use setTimeout to ensure form state is reset before navigation
      setTimeout(() => {
        onSuccess?.();
      }, 10);
    },
  });

  const { data } = useAdminServiceAlertsControllerFindById({
    pathParams: { serviceAlertId: serviceAlertId || "" },
  });

  const formik = useFormik<PatchServiceAlertReqDto>({
    initialValues: {
      title: data?.data?.title || "",
      message: data?.data?.message || "",
    },
    enableReinitialize: true,
    validationSchema: toFormikValidationSchema(patchServiceAlertReqDtoSchema),
    validate: toFormikValidate(patchServiceAlertReqDtoSchema),
    onSubmit: (values) => {
      mutate({
        body: { ...values },
        pathParams: { serviceAlertId },
      });
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    setFieldValue,
    resetForm,
  } = formik;

  return {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    setFieldValue,
    resetForm,
    isLoading,
    data,
  };
};
